import { offersSizeChoice, productSizes, tt } from '@agos/shared'

/**
 * The personalised greeting — what the home page says to somebody it recognises.
 *
 * `GET /customer-auth/personalized-suggestion` answers three things: the customer's name, a **discount
 * card** for the dish they order most or ordered last (with a coupon attached), and a **variety card**
 * suggesting something they have not had. Legacy renders up to two cards from it, each headed by one of
 * **forty** templated sentences.
 *
 * This module had been rendering for nobody: `HomePage.tsx` passed `greetingName={null}` as a literal,
 * so the section a franchise switched on was invisible to every customer — the eighth instance in this
 * port of code written, tested and connected to nothing (FB-C51, now FB-C56).
 *
 * **Why the sentences are data rather than prose in a component.** Every one of the forty is a franchise
 * setting: `content.favoriteTemplates`, `lastOrderedTemplates` and `varietyTemplates` override the
 * defaults, and a restaurant that has written its own gets its own. The defaults here are legacy's,
 * byte for byte, because §8 does not stop being true when a string is playful.
 *
 * **The pick is stable within a day.** See `pickTemplate` for what that changes about legacy, and why.
 */

export interface GreetingCoupon {
  code: string
  type: 'fixed' | 'percent' | null
  value: number | null
  expiresAt: string | null
}

export interface GreetingDiscountCard {
  kind: 'favorite' | 'last_ordered'
  productId: number
  productName: string
  imageUrl: string | null
  categoryId: number | null
  priceCents: number | null
  active: boolean
  coupon: GreetingCoupon | null
}

export interface GreetingVarietyCard {
  productId: number
  productName: string
  imageUrl: string | null
  categoryId: number | null
  categoryName: string | null
  lastOrderedProductName: string | null
}

export interface Greeting {
  firstName: string | null
  discount: GreetingDiscountCard | null
  variety: GreetingVarietyCard | null
}

/** Legacy's `DEFAULT_FAVORITE_TEMPLATES`, in order. */
export const FAVORITE_TEMPLATES = [
  tt('Ahoj {name}! Tvoja obľúbená {product} ťa čaká. 😍'),
  tt('{name}, náš kuchár sa pýta - kedy prídeš na {product}? 👨‍🍳'),
  tt('Ahoj {name}! Vieme, že bez {product} to proste nejde. ❤️'),
  tt('{name}! Znova? A zas {product}? Máme ťa radi. 😄'),
  tt('Psst, {name}… {product} šepká tvoje meno. Budeš ignorovať? 🍕'),
  tt('{name}, tvoja {product} bude na stole skôr, ako domyslíš čo dneska. 🚀'),
  tt('Hej {name}! {product} je tu a čaká len na teba. Nezdvíhaj ho/ju dlho. 😏'),
  tt('Tvoja obľúbená {product} ťa čaká. 😍'),
  tt('Vieme čo miluješ. {product} hovorí za všetko. ❤️'),
  tt('{product} - lebo prečo skúšať niečo iné, keď toto je dokonalé? 👌'),
  tt('Klasika zostáva klasikou. A tvojou klasikou je {product}. 🏆'),
]

/** Legacy's `DEFAULT_LAST_ORDERED_TEMPLATES`, in order. */
export const LAST_ORDERED_TEMPLATES = [
  tt('Ahoj {name}! Naposledy si dal(a) {product} - chceš ešte raz? 🔄'),
  tt('{name}, tvoja posledná voľba? {product}. Dobrý vkus. Ešte raz? 😏'),
  tt('Hej {name}! {product} si si pamätá. A my tiež. Závislosť? 😂'),
  tt('{name}, pamätáme si všetko. Hlavne že si si dal(a) {product}. Opäť? 🤔'),
  tt('{name}! {product} bola skvelá, nie? Neodolaj. 🍴'),
  tt('Naposledy: {product}. Dobrý vkus. Chceš znova? 😏'),
  tt('{product} - posledná objednávka. Náhoda alebo závislosť? 😂'),
  tt('{product} si teba pamätá. Vrátiš sa? 🍕'),
]

/** Legacy's `DEFAULT_VARIETY_TEMPLATES`, in order. */
export const VARIETY_TEMPLATES = [
  tt('Naposledy si mal(a) {lastProduct} - čo tak dnes vyskúšať {product}? 🌟'),
  tt('{lastProduct} bolo skvelé. Ale {product}? To je ďalšia úroveň. 🔥'),
  tt('Zmena je život! Po {lastProduct} skús {product}. Neľutuješ. 😉'),
  tt('{lastProduct} bol hit - ale {product} ťa prekvapí. Odvahu! 💪'),
  tt('Vieš čo je lepšie ako {lastProduct}? {product}. Overené kuchyňou. 👌'),
  tt('{lastProduct} si zjedol(a), výborne! Teraz niečo špeciálne: {product}. 🎉'),
  tt('Po {lastProduct} si zaslúžiš novinku. {product} čaká. 🍽️'),
  tt('Čo tak dnes vyskúšať {product}? Dobrodružstvo čaká! 🎯'),
  tt('{product} - lebo život je príliš krátky na nudné obedy. 😄'),
  tt('Psst… {product} je tajným tipom šéfkuchára. Dnes pre teba. 👨‍🍳'),
  tt('Dnes je ten deň na {product}. Veríme ti. 🌟'),
]

function text(row: Record<string, unknown>, key: string): string | null {
  const value = row[key]
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

function couponOf(row: Record<string, unknown>): GreetingCoupon | null {
  const code = text(row, 'couponCode')
  if (code === null) return null
  const type = row.couponType
  return {
    code,
    type: type === 'fixed' || type === 'percent' ? type : null,
    value: typeof row.couponValue === 'number' ? row.couponValue : null,
    expiresAt: text(row, 'couponExpiresAt'),
  }
}

export function greetingOf(payload: unknown): Greeting | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>

  let discount: GreetingDiscountCard | null = null
  if (typeof row.discount === 'object' && row.discount !== null) {
    const card = row.discount as Record<string, unknown>
    const name = text(card, 'productName')
    const kind = card.type === 'favorite' || card.type === 'last_ordered' ? card.type : null
    // A card the customer cannot be shown a name for is a card nobody can act on.
    if (typeof card.productId === 'number' && name !== null && kind !== null) {
      discount = {
        kind,
        productId: card.productId,
        productName: name,
        imageUrl: text(card, 'imageUrl'),
        categoryId: typeof card.categoryId === 'number' ? card.categoryId : null,
        // Withdrawn from the menu since the order that earned the suggestion: `price` is null and the
        // card links to the category rather than adding a line at a price nobody set.
        priceCents: typeof card.price === 'number' ? Math.round(card.price * 100) : null,
        active: card.active !== false,
        coupon: couponOf(card),
      }
    }
  }

  let variety: GreetingVarietyCard | null = null
  if (typeof row.variety === 'object' && row.variety !== null) {
    const card = row.variety as Record<string, unknown>
    const name = text(card, 'productName')
    if (typeof card.productId === 'number' && name !== null) {
      variety = {
        productId: card.productId,
        productName: name,
        imageUrl: text(card, 'imageUrl'),
        categoryId: typeof card.categoryId === 'number' ? card.categoryId : null,
        categoryName: text(card, 'categoryName'),
        lastOrderedProductName: text(card, 'lastOrderedProductName'),
      }
    }
  }

  if (discount === null && variety === null) return null

  const full = text(row, 'customerName')
  return {
    // Legacy greets by first name alone. *Ahoj Jana Nováková!* is a form letter; *Ahoj Jana!* is not.
    firstName: full === null ? null : (full.split(' ')[0] ?? null),
    discount,
    variety,
  }
}

/**
 * Which of the pool, from a seed — the same template all day, a different one tomorrow.
 *
 * **Legacy's arithmetic is not copied here, and the reason is worth stating.** It is a linear
 * congruential step over `(dayOfYear * 7 + 3) / 100`, plus `Math.random() * 0.01` folded into the seed
 * at mount. The random part is what gives legacy its variety: the day term alone lands on **three**
 * sentences across two months, measured, so thirty written sentences would be nine tenths unused.
 *
 * `Math.random()` during render is impure — the shell's own lint rule refuses it, and it would make the
 * greeting change under a customer mid-read. So the day is mixed properly instead: consecutive days land
 * far apart in the pool, the answer is stable for as long as the day is, and nothing is random.
 *
 * The visible difference from legacy: a customer who reloads the menu twice in an afternoon sees the
 * same sentence rather than two. That is the better half of the trade.
 */
export function pickTemplate(pool: readonly string[], seed: number): string {
  if (pool.length === 0) return ''
  let mixed = Math.floor(seed * 1000) | 0
  mixed = Math.imul(mixed ^ (mixed >>> 15), 0x2c1b3c6d)
  mixed = Math.imul(mixed ^ (mixed >>> 12), 0x297a2d39)
  mixed ^= mixed >>> 15
  return pool[Math.abs(mixed) % pool.length] ?? ''
}

/** The day of the year, which is what the pick is stable within. */
export function dayOfYear(now: Date): number {
  const start = new Date(now.getFullYear(), 0, 0)
  return Math.floor((now.getTime() - start.getTime()) / 86_400_000)
}

/**
 * A template that can actually be filled.
 *
 * A greeting with `{name}` in it and no name to put there reads *Ahoj ! …*, so those are filtered out
 * rather than substituted with a blank — and the pool falls back to the whole list only if filtering
 * leaves nothing, because a slightly wrong sentence beats no sentence.
 */
export function eligibleTemplates(
  pool: readonly string[],
  options: { hasName: boolean; hasLastProduct: boolean },
): string[] {
  const usable = pool.filter((template) => {
    if (!options.hasName && template.includes('{name}')) return false
    if (!options.hasLastProduct && template.includes('{lastProduct}')) return false
    return true
  })
  return usable.length > 0 ? usable : [...pool]
}

export function fillTemplate(
  template: string,
  values: { name?: string | null; product?: string | null; lastProduct?: string | null },
): string {
  return template
    .replace(/\{name\}/g, values.name ?? '')
    .replace(/\{product\}/g, values.product ?? '')
    .replace(/\{lastProduct\}/g, values.lastProduct ?? '')
}

/** The sentence over the discount card, chosen and filled. */
export function discountGreeting(greeting: Greeting, now: Date, templates?: readonly string[]): string {
  const card = greeting.discount
  if (card === null) return ''
  const pool =
    templates ?? (card.kind === 'favorite' ? FAVORITE_TEMPLATES : LAST_ORDERED_TEMPLATES)
  const usable = eligibleTemplates(pool, {
    hasName: greeting.firstName !== null,
    hasLastProduct: false,
  })
  // Legacy's seed for this card, to the digit.
  const seed = (dayOfYear(now) * 7 + 3) / 100
  return fillTemplate(pickTemplate(usable, seed), {
    name: greeting.firstName,
    product: card.productName,
  })
}

/** The sentence over the variety card. */
export function varietyGreeting(greeting: Greeting, now: Date, templates?: readonly string[]): string {
  const card = greeting.variety
  if (card === null) return ''
  const usable = eligibleTemplates(templates ?? VARIETY_TEMPLATES, {
    hasName: greeting.firstName !== null,
    hasLastProduct: card.lastOrderedProductName !== null,
  })
  const seed = (dayOfYear(now) * 13 + 11) / 100
  return fillTemplate(pickTemplate(usable, seed), {
    name: greeting.firstName,
    product: card.productName,
    lastProduct: card.lastOrderedProductName,
  })
}

/** *20 % zľava* or *2,00 € zľava* — what the attached coupon is worth, in the customer's terms. */
export function couponNote(coupon: GreetingCoupon | null, formatMoney: (cents: number) => string): string {
  if (coupon === null || coupon.value === null || coupon.value <= 0) return ''
  if (coupon.type === 'percent') return `${String(coupon.value)} % ${tt('zľava')}`
  if (coupon.type === 'fixed') return `${formatMoney(Math.round(coupon.value * 100))} ${tt('zľava')}`
  return ''
}

/**
 * Whether the card offers to add the dish, or only to go and look at it.
 *
 * No price means the product has left the menu since the order that earned the suggestion. Legacy sends
 * the customer to the category instead of adding a line at a price nobody set — the same rule as
 * FB-C29's reorder, for the same reason.
 */
export function mayAddDirectly(card: GreetingDiscountCard): boolean {
  return card.priceCents !== null && card.active
}

/**
 * A product row as the menu holds it - only the fields the web menu's own filters read.
 *
 * `GET /products` answers every product, the hidden ones included, and `HomePage` filters on the client: so the card
 * must ask the same questions of the same rows rather than trust the suggestion's id.
 */
export interface MenuProduct {
  id: number
  active?: boolean
  visibleOnWeb?: boolean
  orderable?: boolean
  categoryId?: number | null
  pizzaSizes?: string | null
}

/**
 * The product the menu offers for a card's id, or null - FRONTEND.md §9.776, owner O137.
 *
 * *"personalizovaná ponuka robí kktiny, že sa dajú objednať veci, ktoré tam nie sú, respektíve sú iba v manageri"*.
 * `HomePage`'s own three filters: the product `active`, on the web, and in a category the menu lists (the caller passes
 * the ids of the categories it draws, which are already filtered on `visibleOnWeb`). `!== false`, as the menu does, so
 * a row from before a field existed still counts. `orderable` is **not** one of them - a dish the kitchen has run out
 * of stays on the menu, greyed, and the card says so the same way ({@link discountAction}).
 */
export function webMenuProduct<T extends MenuProduct>(
  productId: number,
  products: readonly T[],
  menuCategoryIds: ReadonlySet<number>,
): T | null {
  const product = products.find((candidate) => candidate.id === productId)
  if (product === undefined) return null
  if (product.active === false || product.visibleOnWeb === false) return null
  if (typeof product.categoryId !== 'number' || !menuCategoryIds.has(product.categoryId)) return null
  return product
}

/**
 * What the discount card's button does - FRONTEND.md §9.776.
 *
 * - `look`: the server sent no price or an inactive product (a server older than BACKEND.md §10A.430): open the
 *   category, as before.
 * - `unavailable`: the kitchen has switched it off - said, and not added, as the menu's tiles say it (§9.233).
 * - `choose-size`: it comes in more than one size, so the category opens and the customer picks one. Adding a line with
 *   no size would bill whatever the server defaults to - FB-796's charge in a new place.
 * - `closed`: outside opening hours, the home tiles' own gate, so the basket does not fill for a checkout that will
 *   refuse it.
 * - `add`: through the menu's own add, which asks the add-on question every other add asks.
 */
export type DiscountAction = 'look' | 'unavailable' | 'choose-size' | 'closed' | 'add'

export function discountAction(
  card: GreetingDiscountCard,
  product: MenuProduct,
  mayOrder: boolean,
): DiscountAction {
  if (!mayAddDirectly(card)) return 'look'
  if (product.orderable === false) return 'unavailable'
  if (offersSizeChoice(productSizes(product))) return 'choose-size'
  if (!mayOrder) return 'closed'
  return 'add'
}
